import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "med-spa-web-design-colorado",
  metaTitle: "Med Spa Web Design in Colorado | Websites That Book Jobs",
  metaDescription:
    "Custom med spa web design across Colorado — fast, mobile-first, conversion-tested sites built to turn local searches into booked work. Owner-led. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Medical practice marketing", to: "/medical-marketing-colorado" },
  ],
  currentLabel: "Med Spa Web Design Colorado",
  eyebrow: "Med Spa Web Design — Colorado",
  h1: (
    <>
      Med Spa <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded med spa websites that load in under 2 seconds, convert mobile searches, and rank from day one.",
  intro:
    "Med Spa websites lose the lead in the first 4 seconds on a phone. We hand-code fast, mobile-first med spa sites with sticky CTAs, dedicated service pages, and conversion-tested intake flows. No bloated templates, no page builders — just a fast custom site that turns local searches into booked work.",
  definition: {
    term: "Med Spa web design in Colorado",
    body: "Med Spa web design is the practice of building conversion-focused, mobile-first websites for med spa businesses — with dedicated service pages, clear CTAs, fast load times, and proper local SEO structure baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load with gallery-heavy hero that doesn't tank Core Web Vitals." },
    { title: "Treatment service pages", body: "Botox, fillers, laser, microneedling, body contouring, weight loss — each ranks independently." },
    { title: "Before/after galleries", body: "Properly consented galleries with image schema and lazy loading." },
    { title: "Online booking + memberships", body: "Integration with Boulevard, Vagaro, or your platform for real-time booking and membership signup." },
    { title: "Provider bio pages", body: "Injectors, RNs, and MDs each with credentials and direct booking CTA." },
    { title: "Schema and SEO at launch", body: "MedicalBusiness, Service, FAQPage, Review schema baked in." },
  ],
  whyThisMarket: [
    { heading: "Visual proof is the entire close", body: "Med spa is a portfolio sale — before/after galleries above the fold close the booking." },
    { heading: "Treatment-specific pages outrank \"med spa near me\"", body: "Searchers type \"Botox\", \"lip filler\", \"laser hair removal\" — build a ranking page for each." },
    { heading: "Memberships drive LTV", body: "Membership signup CTAs above the fold drive 3x customer lifetime value." },
  ],
  relatedLinks: [
    { to: "/medical-marketing-colorado", label: "Medical practice marketing hub" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/web-design-pricing", label: "Web design pricing" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado case studies" },
    { to: "/wix-vs-custom-website-colorado-med-spa", label: "Wix vs custom website for Med spa" },
    { to: "/squarespace-vs-custom-website-colorado-med-spa", label: "Squarespace vs custom website for Med spa" },
    { to: "/godaddy-vs-custom-website-colorado-med-spa", label: "GoDaddy vs custom website for Med spa" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a med spa website cost in Colorado?", a: "Website packages start at $1,850 one-time. All include one year of hosting and a built-in traffic dashboard. Extra pages are $85 each. Compare all packages or build a plan on our pricing page." },
    { q: "How long does it take to build a med spa website?", a: "Typical timeline is 4–6 weeks for a 12–20 page med spa site with full service and service-area pages. Larger multi-location med spa businesses can take 8–10 weeks." },
    { q: "Will the new site rank from day one?", a: "Existing rankings stay or improve because we build proper 301 redirects from every old URL, keep your domain, and ship with full schema and on-page SEO from launch — not as a phase 2 afterthought." },
    { q: "Do you only serve Colorado Springs med spa businesses?", a: "No — we build med spa sites for businesses across all of Colorado: Denver, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Med Spa Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for med spa businesses across Colorado. Fast, conversion-focused, and SEO-ready from launch.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/med-spa-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
